/**
 * Task 4.6 — a game may have more slots than it has prize types, up to six,
 * with the extra slots filled by repeating the prizes in turn.
 *
 * Why this exists: a wheel built one-slot-per-prize looks broken with two
 * prizes. Two enormous half-circles do not read as a prize wheel, and a guest
 * seeing them assumes the game is unfinished. Six slots is where a wheel starts
 * looking like a wheel, so anything under six repeats its prizes round-robin —
 * A B → A B A B A B, A B C → A B C A B C, A B C D → A B C D A B.
 *
 * ── The important part: this changes nothing about who wins ──────────────
 * Odds still come from `pickPrize` over the DISTINCT prizes, weighted by
 * rating. A repeated prize does NOT get a second chance; the wheel simply lands
 * on one of the slots that shows it (`slotsFor`). Duplicating slots and
 * duplicating odds would silently make common prizes commoner, which is the
 * kind of bug nobody notices until the stock runs out early.
 *
 * More than six prize types is not padded — every type keeps its own slot,
 * because hiding a real prize to respect a cosmetic maximum is worse than a
 * busy wheel.
 */
export const MAX_SLOTS = 6;

export interface Slot<T> {
  item: T;
  /** Index of this slot's prize in the original (distinct) list. */
  sourceIndex: number;
}

export function fillSlots<T>(items: T[], max: number = MAX_SLOTS): Slot<T>[] {
  if (items.length === 0) return [];
  const slots: Slot<T>[] = items.map((item, sourceIndex) => ({ item, sourceIndex }));
  for (let i = items.length; i < max; i++) {
    const sourceIndex = i % items.length;
    slots.push({ item: items[sourceIndex], sourceIndex });
  }
  return slots;
}

/** How many slots `n` prize types will render as. */
export function slotCount(n: number, max: number = MAX_SLOTS): number {
  return n === 0 ? 0 : Math.max(n, max);
}

/** Every slot index showing the prize at `sourceIndex`. */
export function slotsFor<T>(slots: Slot<T>[], sourceIndex: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < slots.length; i++) {
    if (slots[i].sourceIndex === sourceIndex) out.push(i);
  }
  return out;
}
